import PersonIcon from "@mui/icons-material/Person";
import { Button, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { isUserAuthorizedByServer } from "../../api/userAccess";
import PageLoading from "../../components/PageLoading";
import { logout } from "../../util/authentication";
import { selectTechOpsUserSession } from "../../slices/techOpsUserSession/techOpsUserSession";

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: "hidden",
  },
  pageTitle: {
    [theme.breakpoints.down("lg")]: {
      margin: "auto",
      marginTop: "20px",
      marginLeft: "15px",
      fontSize: "20px",
    },
    fontSize: "32px",
    marginTop: "10px",
    marginLeft: "75px",
    textAlign: "left",
    color: theme.palette.primary.swaBlue,
  },
  quickLinksButtonGroup: {
    [theme.breakpoints.down("md")]: {
      margin: "auto",
    },
    display: "flex",
    justifyContent: "left",
    marginLeft: "75px",
    width: "100%",
    flexWrap: "wrap",
    color: theme.palette.secondary.swaWhite,
  },
  quickLinksButton: {
    [theme.breakpoints.down("md")]: {
      height: "36px",
      minWidth: "95vw",
      margin: "auto",
      marginTop: "5px",
      marginBottom: "5px",
    },
    width: "250px",
    height: "50px",
    color: theme.palette.secondary.swaWhite,
    fontSize: "14px",
    margin: "10px",
    paddingLeft: "40px",
    paddingRight: "40px",
    lineHeight: "16px",
  },
  yourInfo: {
    [theme.breakpoints.down("md")]: {
      minWidth: "95vw",
      margin: "auto",
    },
    width: "22vw",
  },
  yourInfoTitle: {
    [theme.breakpoints.down("md")]: {
      marginLeft: "10px",
      marginTop: "5px",
    },
    fontSize: "20px",
    textAlign: "left",
    marginTop: "10px",
    marginLeft: "15px",
    fontWeight: "bold",
  },
  yourInfoTable: {
    [theme.breakpoints.down("md")]: {
      minWidth: "87vw",
      margin: "auto",
    },
    margin: "auto",
    width: "19vw",
    fontSize: "16px",
  },
  yourInfoTableDivider: {
    width: "19vw",
  },
  leftColumn: {
    textAlign: "left",
  },
  rightColumn: {
    textAlign: "right",
  },
  quickLinksDivider: {
    marginTop: "10px",
    marginBottom: "10px",
    marginLeft: "1%",
    marginRight: "1%",
  },
  buttonIcon: {
    position: "absolute",
    left: "10px",
  },
  superuserButtonIcon: {
    position: "absolute",
    left: "10px",
    color: theme.palette.primary.swaYellow,
  },
  toolTip: {
    fontSize: "12px",
    maxWidth: "200px",
    textAlign: "center",
  },
}));

const HomePage = (props) => {
  const classes = useStyles();
  const techOpsUserSession = useSelector(selectTechOpsUserSession);
  const [isUserAuthorizedState, setIsUserAuthorizedState] = useState(false);
  const [isUserAuthorizedStateLoading, setIsUserAuthorizedStateLoading] = useState(true);

  useEffect(() => {
    if (!isUserAuthorizedState) {
      isUserAuthorized();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserAuthorizedState]);

  const isUserAuthorized = () => {
    isUserAuthorizedByServer(techOpsUserSession).then((is200) => {
      if (is200) {
        setIsUserAuthorizedState(true);
      } else {
        logout();
      }
      setIsUserAuthorizedStateLoading(false);
    });
  };
  const renderPageHeader = () => {
    return (
      <div>
        <Grid item xs={12}>
          <Typography component={"div"}>
            <Box className={classes.pageTitle}>{"Welcome!"}</Box>
          </Typography>
        </Grid>
        <Divider className="divider" />
      </div>
    );
  };

  const renderPageContent = () => {
    const quickLinksMenuList = () => {
      let quickLinks = [];
      const userMenuItems = props.userFeatures;
      if (props.isDesktopView) {
        userMenuItems.forEach((menuItem) => {
          if (menuItem.isHomeButton) {
            quickLinks.push(menuItem);
          } else {
            if (menuItem.childItems.length > 0) {
              menuItem.childItems.forEach((childItem) => {
                if (childItem.isHomeButton) {
                  quickLinks.push(childItem);
                }
              });
            }
          }
        });
      } else {
        userMenuItems.forEach((menuItem) => {
          if (menuItem.isActiveOnMobile) {
            if (menuItem.isHomeButton) {
              quickLinks.push(menuItem);
            } else {
              if (menuItem.childItems.length > 0) {
                menuItem.childItems.forEach((childItem) => {
                  if (childItem.isHomeButton && childItem.isActiveOnMobile) {
                    quickLinks.push(childItem);
                  }
                });
              }
            }
          }
        });
      }
      quickLinks.sort((a, b) => (a.label > b.label ? 1 : b.label > a.label ? -1 : 0));
      return quickLinks;
    };

    const renderPath = (item) => {
      let path = item.path;

      return path;
    };

    const renderEmployeeQuickLinks = () => {
      return quickLinksMenuList().map((item, idx) => {
          return (
            <Tooltip key={idx} title={item.description} arrow placement="bottom" classes={{ tooltip: classes.toolTip }}>
              <Button
                className={classes.quickLinksButton}
                href={`#${renderPath(item)}`}
                variant="contained"
                color="primary"
                size="large"
                aria-label="large contained primary button group"
              >
                <PersonIcon className={classes.buttonIcon} />
                {item.label}
              </Button>
            </Tooltip>
          );
      });
    };

    return (
      <Grid container className={classes.root}>
        <Grid item xs={12} sm={12} md={9}>
          <Grid container>
            <Grid item xs={12} className={classes.quickLinksButtonGroup}>
              {renderEmployeeQuickLinks()}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={3}>
          <Paper className={classes.yourInfo} variant="outlined">
            <div className={classes.yourInfoTitle}>My Info</div>
            <Grid container className={classes.yourInfoTable}>
              <Grid item xs={6} className={classes.leftColumn}>
                Employee ID
              </Grid>
              <Grid item xs={6} className={classes.rightColumn}>
                {techOpsUserSession.eid}
              </Grid>
              <Grid item xs={12}>
                <Divider />
              </Grid>   
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  return (
    <div className="App-contents enable-scroll" data-testid="home-page">
      {isUserAuthorizedStateLoading ? (
        <PageLoading />
      ) : (
        <div>
          {renderPageHeader()}
          {renderPageContent()}
        </div>
      )}
    </div>
  );
};

export default HomePage;
